/**
 * Map Module Alpine.js Component
 */

(() => {
    // Global callback for Google Maps API - define it immediately
    if (!window.initGoogleMaps) {
        window.initGoogleMaps = function () {
            console.log('Google Maps API loaded successfully');
            // Dispatch custom event to notify components
            window.dispatchEvent(new CustomEvent('googleMapsLoaded'));
        };
    }

    // Also define it as a fallback in case the script loads before this
    window.initGoogleMaps =
        window.initGoogleMaps ||
        function () {
            console.log('Google Maps API loaded successfully (fallback)');
            window.dispatchEvent(new CustomEvent('googleMapsLoaded'));
        };

    function registerMapModule() {
        Alpine.data('mapModuleManager', () => ({
            searchString: '',
            map: null,
            markers: [],
            markerClusterer: null,
            oms: null,
            hoverInfo: null,
            groupedPois: {},

            noResults: false,

            showModal: false,
            selectedPoi: {},

            showVideoModal: false,
            activeVideo: {},

            // Preload management
            preloadQueue: [],
            preloadTimeout: null,
            isPreloading: false,

            // Search debouncing
            searchTimeout: null,
            isSearching: false,

            init() {
                this.$nextTick(() => {
                    this.waitForGoogleMaps()
                        .then(() => {
                            this.initializeMap();

                            this.hoverInfo = new google.maps.InfoWindow({
                                disableAutoPan: true,
                                pixelOffset: new google.maps.Size(0, -8),
                                maxWidth: 200,
                            });

                            // initial load (all POIs)
                            this.searchLocation();
                        })
                        .catch((error) => {
                            console.error('Failed to initialize Google Maps:', error);
                            this.showMapError();
                        });
                });
            },

            waitForGoogleMaps() {
                return new Promise((resolve, reject) => {
                    // Check if Google Maps is already loaded
                    if (window.google && window.google.maps && window.google.maps.Map) {
                        resolve();
                        return;
                    }

                    // Listen for the custom event
                    const handleMapsLoaded = () => {
                        window.removeEventListener('googleMapsLoaded', handleMapsLoaded);
                        resolve();
                    };

                    window.addEventListener('googleMapsLoaded', handleMapsLoaded);

                    // Fallback: Wait for Google Maps to load with polling
                    let attempts = 0;
                    const maxAttempts = 100; // 10 seconds max wait (increased for Firefox)
                    const checkInterval = setInterval(() => {
                        attempts++;

                        if (window.google && window.google.maps && window.google.maps.Map) {
                            clearInterval(checkInterval);
                            window.removeEventListener('googleMapsLoaded', handleMapsLoaded);
                            resolve();
                        } else if (attempts >= maxAttempts) {
                            clearInterval(checkInterval);
                            window.removeEventListener('googleMapsLoaded', handleMapsLoaded);
                            reject(new Error('Google Maps API failed to load within timeout'));
                        }
                    }, 100);
                });
            },

            showMapError() {
                const container = document.querySelector('.map-module__container');
                if (container) {
                    container.innerHTML = `
                        <div class="flex items-center justify-center h-full bg-gray-100 text-gray-600">
                            <div class="text-center">
                                <svg class="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                                </svg>
                                <h3 class="text-lg font-medium mb-2">Térkép betöltési hiba</h3>
                                <p class="text-sm">A térkép nem tölthető be. Kérjük, frissítse az oldalt vagy próbálja újra később.</p>
                                <button onclick="location.reload()" class="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
                                    Oldal frissítése
                                </button>
                            </div>
                        </div>
                    `;
                }
            },

            initializeMap(isSearchContext = false) {
                const container = document.querySelector('.map-module__container');
                if (!container) {
                    console.error('Map container not found');
                    return;
                }

                // Check if Google Maps API is available
                if (!window.google || !window.google.maps || !window.google.maps.Map) {
                    console.error('Google Maps API not available');
                    this.showMapError();
                    return;
                }

                // Check if ControlPosition is available (Firefox specific issue)
                if (!window.google.maps.ControlPosition) {
                    console.warn('Google Maps ControlPosition not available, using fallback');
                    // Don't return, just continue without ControlPosition
                }

                // Custom map style JSON
                const customMapStyle = window.mapAjax.map_style || null;

                // Hungary bounds
                const hungaryBounds = {
                    north: 49.0,
                    south: 45.5,
                    east: 23.2,
                    west: 15.8,
                };

                // Create the map with error handling
                try {
                    // Create map options with fallback for Firefox
                    const mapOptions = {
                        center: { lat: 47.17, lng: 19.5 },
                        zoom: 7,
                        mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: false,
                        cameraControl: false,
                        zoomControl: true,
                        styles: customMapStyle,
                        // restriction: {
                        //     latLngBounds: hungaryBounds,
                        //     strictBounds: false,
                        // },
                    };

                    // Add zoom control options only if ControlPosition is available
                    if (
                        window.google &&
                        window.google.maps &&
                        window.google.maps.ControlPosition &&
                        window.google.maps.ZoomControlStyle
                    ) {
                        try {
                            mapOptions.zoomControlOptions = {
                                position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
                                style: window.google.maps.ZoomControlStyle.LARGE,
                            };
                        } catch (error) {
                            console.warn('Error setting zoom control options:', error);
                            // Continue without zoom control options
                        }
                    } else {
                        console.warn(
                            'ControlPosition or ZoomControlStyle not available, using default zoom control'
                        );
                    }

                    this.map = new google.maps.Map(container, mapOptions);
                } catch (error) {
                    console.error('Error creating Google Map:', error);
                    this.showMapError();
                    return;
                }

                // Add custom loading overlay to hide Google Maps default gray loading
                const loadingOverlay = document.createElement('div');
                loadingOverlay.className = 'google-maps-loading-overlay';
                loadingOverlay.style.cssText = `
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: #087234;
                    z-index: 1000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                `;
                loadingOverlay.innerHTML = `
                    <div class="text-center">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                        <span class="text-sm">Térkép betöltése...</span>
                    </div>
                `;
                container.appendChild(loadingOverlay);

                // Remove the loading overlay when map is ready
                google.maps.event.addListenerOnce(this.map, 'idle', () => {
                    if (loadingOverlay.parentNode) {
                        loadingOverlay.parentNode.removeChild(loadingOverlay);
                    }
                });

                // Only fit Hungary into view if we're not in a search context
                // In search context, the plotPois function will handle the zoom
                if (!isSearchContext) {
                    const hungarySW = new google.maps.LatLng(
                        hungaryBounds.south,
                        hungaryBounds.west
                    );
                    const hungaryNE = new google.maps.LatLng(
                        hungaryBounds.north,
                        hungaryBounds.east
                    );
                    const hungaryBoundsObj = new google.maps.LatLngBounds(hungarySW, hungaryNE);
                    this.map.fitBounds(hungaryBoundsObj);
                }
            },

            clearMarkers() {
                // Clear all existing markers from the map
                this.markers.forEach((marker, index) => {
                    if (marker && marker.setMap) {
                        try {
                            // Force remove from map
                            marker.setMap(null);

                            // Also try to remove all listeners
                            google.maps.event.clearInstanceListeners(marker);
                        } catch (error) {
                            console.error(`Error removing marker ${index}:`, error);
                        }
                    }
                });

                // Clear the markers array
                this.markers = [];

                // Also clear the grouped POIs to ensure the list is cleared
                this.groupedPois = {};
                this.noResults = false;
            },

            // Super aggressive marker clearing
            superAggressiveClearMarkers() {
                // Store current search context
                const currentSearchString = this.searchString.trim();
                const hasSearch = currentSearchString.length > 0;

                // Clear our tracked markers
                this.markers.forEach((marker, index) => {
                    if (marker && marker.setMap) {
                        try {
                            marker.setMap(null);
                            google.maps.event.clearInstanceListeners(marker);
                        } catch (error) {
                            console.error(`Error removing marker ${index}:`, error);
                        }
                    }
                });

                // Clear the container and recreate map
                const container = document.querySelector('.map-module__container');
                if (container) {
                    // Clear content but keep the background color
                    container.innerHTML = '';

                    // Add loading overlay to hide Google Maps gray flash during recreation
                    const loadingOverlay = document.createElement('div');
                    loadingOverlay.className = 'google-maps-loading-overlay';
                    loadingOverlay.style.cssText = `
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background-color: #087234;
                        z-index: 1000;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: white;
                    `;
                    loadingOverlay.innerHTML = `
                        <div class="text-center">
                            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                            <span class="text-sm">Térkép frissítése...</span>
                        </div>
                    `;
                    container.appendChild(loadingOverlay);
                }

                // Reset arrays
                this.markers = [];
                this.groupedPois = {};
                this.noResults = false;

                // Reinitialize the map with search context
                this.waitForGoogleMaps()
                    .then(() => {
                        this.initializeMap(hasSearch);
                    })
                    .catch((error) => {
                        console.error('Failed to reinitialize Google Maps:', error);
                        this.showMapError();
                    });
            },

            searchLocation() {
                // Clear any existing search timeout
                if (this.searchTimeout) {
                    clearTimeout(this.searchTimeout);
                }

                // Set a new timeout to debounce the search
                this.searchTimeout = setTimeout(() => {
                    this.performSearch();
                }, 300); // 300ms debounce
            },

            performSearch() {
                // Prevent multiple simultaneous searches
                if (this.isSearching) {
                    return;
                }

                this.isSearching = true;

                // Use super aggressive clearing to ensure markers are completely removed
                this.superAggressiveClearMarkers();

                const city = this.searchString.trim();
                const params = new URLSearchParams({
                    action: 'get_map_pois',
                    nonce: window.mapAjax.nonce,
                    city,
                });

                fetch(window.mapAjax.ajaxurl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: params.toString(),
                })
                    .then((r) => r.json())
                    .then((json) => {
                        if (!json.success) {
                            console.error(json.data);
                            this.isSearching = false;
                            return;
                        }
                        const pois = json.data;

                        if (pois.length === 0) {
                            // no results
                            this.noResults = true;
                        } else {
                            this.noResults = false;
                            this.plotPois(pois);
                        }
                        this.isSearching = false;
                    })
                    .catch((error) => {
                        console.error('Search error:', error);
                        this.isSearching = false;
                    });
            },

            clearSearch() {
                this.searchString = '';
                this.noResults = false;

                // Clear any pending search
                if (this.searchTimeout) {
                    clearTimeout(this.searchTimeout);
                }

                // Perform search immediately to show all POIs
                this.performSearch();
            },

            plotPois(pois) {
                // Check if Google Maps API is available
                if (!window.google || !window.google.maps || !window.google.maps.Map) {
                    console.error('Google Maps API not available in plotPois');
                    return;
                }

                // Store POIs for later reference
                this.pois = pois;
                const grouped = {};

                // Custom marker icon
                const customIcon = {
                    path: google.maps.SymbolPath.CIRCLE,
                    fillColor: '#FF0000',
                    fillOpacity: 1,
                    strokeColor: '#FF0000',
                    strokeWeight: 1,
                    scale: 10,
                    anchor: new google.maps.Point(0, 0),
                };

                // Store original console methods
                const originalConsoleWarn = console.warn;
                const originalConsoleError = console.error;
                const originalConsoleLog = console.log;

                // Suppress deprecation warning for now (Marker API is still supported)
                // This is now handled in the combined console.warn function below

                // Suppress CORS warnings for Google Maps API (these are normal)
                console.error = function (message) {
                    if (
                        typeof message === 'string' &&
                        message.includes('Cross-Origin Request Blocked') &&
                        message.includes('maps.googleapis.com')
                    ) {
                        return; // Suppress CORS warnings for Google Maps
                    }
                    originalConsoleError.apply(console, arguments);
                };

                // Suppress SES (Secure EcmaScript) warnings (these are from browser extensions)
                console.log = function (message) {
                    if (
                        typeof message === 'string' &&
                        (message.includes('SES Removing unpermitted intrinsics') ||
                            message.includes('lockdown-install.js'))
                    ) {
                        return; // Suppress SES warnings
                    }
                    originalConsoleLog.apply(console, arguments);
                };

                // Also suppress jQuery layout warnings in console.warn
                console.warn = function (message) {
                    if (
                        typeof message === 'string' &&
                        (message.includes('google.maps.Marker is deprecated') ||
                            message.includes('Layout was forced before the page was fully loaded'))
                    ) {
                        return; // Suppress these warnings
                    }
                    originalConsoleWarn.apply(console, arguments);
                };

                pois.forEach((poi) => {
                    // Marker
                    const marker = new google.maps.Marker({
                        position: poi.location,
                        map: this.map,
                        title: poi.title,
                        animation: google.maps.Animation.DROP,
                        icon: customIcon,
                    });

                    // Store POI data directly on the marker for easy access
                    marker.poiData = poi;

                    // Hover tooltip
                    marker.addListener('mouseover', () => {
                        this.hoverInfo.setContent(`
                          <div class="min-w-36">
                            <strong>${poi.title}</strong><br>
                            <em>${poi.venue}</em>
                          </div>
                        `);
                        this.hoverInfo.open(this.map, marker);

                        // Preload videos on hover for faster modal opening
                        this.preloadPoiVideos(poi);
                    });
                    marker.addListener('mouseout', () => {
                        this.hoverInfo.close();
                        // Cancel preload if mouse leaves
                        this.cancelPreloadPoiVideos(poi);
                    });
                    // Note: Click events are handled by OMS, not directly on markers

                    this.markers.push(marker);

                    // Group for list
                    if (!grouped[poi.city]) {
                        grouped[poi.city] = [];
                    }
                    grouped[poi.city].push(poi);
                });

                this.groupedPois = grouped;

                // Mindig teljes Magyarország nézetet mutatunk, hogy láthatóak legyenek a pontok
                // Ez megbízhatóbb, mint a zoom-ot próbálni
                const hungaryBounds = {
                    north: 49.0,
                    south: 45.5,
                    east: 23.2,
                    west: 15.8,
                };
                const sw = new google.maps.LatLng(hungaryBounds.south, hungaryBounds.west);
                const ne = new google.maps.LatLng(hungaryBounds.north, hungaryBounds.east);
                this.map.fitBounds(new google.maps.LatLngBounds(sw, ne));

                this.markerClusterer = new markerClusterer.MarkerClusterer({
                    markers: this.markers,
                    map: this.map,
                    renderer: {
                        render: this.markerClustererRenderer.bind(this),
                    },
                });

                // Initialize OverlappingMarkerSpiderfier
                this.oms = new OverlappingMarkerSpiderfier(this.map, {
                    markersWontMove: true,
                    markersWontHide: true,
                    keepSpiderfied: true,
                });

                // Add all markers to OMS
                this.markers.forEach((marker) => {
                    this.oms.addMarker(marker);
                });

                // Initialize overlapping marker icons
                this.initializeOverlappingMarkers();

                // Set up OMS event listeners
                this.oms.addListener('click', (marker, event) => {
                    // Get POI data directly from the marker
                    if (marker.poiData) {
                        this.openPoi(marker.poiData);
                    }
                });

                this.oms.addListener('spiderfy', (markers) => {
                    // When spiderfied, show individual markers with original icons
                    markers.forEach((marker) => {
                        marker.setIcon(this.getOriginalMarkerIcon());
                    });
                });

                this.oms.addListener('unspiderfy', (markers) => {
                    // When unspiderfied, show count icon for overlapping markers
                    this.updateOverlappingMarkers();
                });
            },

            markerClustererRenderer({ count, position }, stats, map) {
                const color = '#ff0000';

                // create svg literal with fill color
                const svg = `<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="50" height="50">
                    <circle cx="120" cy="120" opacity=".6" r="70" />
                    <circle cx="120" cy="120" opacity=".3" r="90" />
                    <circle cx="120" cy="120" opacity=".2" r="110" />
                    <text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="50" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${count}</text>
                    </svg>`;
                const title = `Cluster of ${count} markers`,
                    zIndex = Number(google.maps.Marker.MAX_ZINDEX) + count;

                const clusterOptions = {
                    position,
                    zIndex,
                    title,
                    icon: {
                        url: `data:image/svg+xml;base64,${btoa(svg)}`,
                        anchor: new google.maps.Point(25, 25),
                    },
                };

                return new google.maps.Marker(clusterOptions);
            },

            getOriginalMarkerIcon() {
                return {
                    path: google.maps.SymbolPath.CIRCLE,
                    fillColor: '#FF0000',
                    fillOpacity: 1,
                    strokeColor: '#FF0000',
                    strokeWeight: 1,
                    scale: 10,
                    anchor: new google.maps.Point(0, 0),
                };
            },

            initializeOverlappingMarkers() {
                // Find overlapping markers and set count icons
                const overlappingGroups = this.findOverlappingMarkers();
                overlappingGroups.forEach((group) => {
                    if (group.length > 1) {
                        group.forEach((marker) => {
                            const countIcon = this.createCountIcon(group.length);
                            marker.setIcon(countIcon);
                        });
                    }
                });
            },

            updateOverlappingMarkers() {
                // Reset all markers to original icons first
                this.markers.forEach((marker) => {
                    marker.setIcon(this.getOriginalMarkerIcon());
                });

                // Then apply count icons to overlapping groups
                this.initializeOverlappingMarkers();
            },

            findOverlappingMarkers() {
                const groups = [];
                const processed = new Set();
                const nearbyDistanceDegrees = 0.001; // approximately 100 meters

                this.markers.forEach((marker) => {
                    if (processed.has(marker)) return;

                    const group = [marker];
                    const position = marker.getPosition();
                    const lat1 = position.lat();
                    const lng1 = position.lng();

                    // Find other markers within nearby distance
                    this.markers.forEach((otherMarker) => {
                        if (otherMarker === marker || processed.has(otherMarker)) return;

                        const otherPosition = otherMarker.getPosition();
                        const lat2 = otherPosition.lat();
                        const lng2 = otherPosition.lng();

                        // Calculate simple distance in degrees
                        const latDiff = Math.abs(lat1 - lat2);
                        const lngDiff = Math.abs(lng1 - lng2);
                        const distance = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);

                        // If within nearby distance
                        if (distance < nearbyDistanceDegrees) {
                            group.push(otherMarker);
                        }
                    });

                    if (group.length > 1) {
                        group.forEach((m) => processed.add(m));
                        groups.push(group);
                    }
                });

                return groups;
            },

            createCountIcon(count) {
                // Create a distinctive icon for overlapping markers
                const color = '#FF6B6B'; // Red color for overlapping markers

                // Create SVG with count
                const svg = `<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="40" height="40">
                    <circle cx="50" cy="50" r="45" opacity="0.9" stroke="#fff" stroke-width="3"/>
                    <circle cx="50" cy="50" r="35" opacity="0.8" stroke="#fff" stroke-width="2"/>
                    <text x="50%" y="50%" style="fill:#fff;font-weight:bold" text-anchor="middle" font-size="20" dominant-baseline="middle" font-family="Arial,sans-serif">${count}</text>
                </svg>`;

                return {
                    url: `data:image/svg+xml;base64,${btoa(svg)}`,
                    scaledSize: new google.maps.Size(40, 40),
                    anchor: new google.maps.Point(20, 20),
                };
            },

            openPoi(poi) {
                this.selectedPoi = poi;
                this.showModal = true;

                // Only load videos if not already loaded
                if (!poi.mux_videos && poi.video_count > 0) {
                    this.loadPoiVideos(poi.id);
                }
            },

            // Preload videos on hover for faster modal opening
            preloadPoiVideos(poi) {
                // Skip if already loaded or no videos
                if (poi.mux_videos || poi.video_count === 0) {
                    return;
                }

                // Clear any existing timeout for this POI
                if (poi.preloadTimeout) {
                    clearTimeout(poi.preloadTimeout);
                }

                // Set a timeout to preload after 300ms of hover
                poi.preloadTimeout = setTimeout(() => {
                    // Add to preload queue
                    if (!this.preloadQueue.find((item) => item.id === poi.id)) {
                        this.preloadQueue.push(poi);
                    }
                    this.processPreloadQueue();
                }, 300); // 300ms hover delay
            },

            // Cancel preload when mouse leaves
            cancelPreloadPoiVideos(poi) {
                if (poi.preloadTimeout) {
                    clearTimeout(poi.preloadTimeout);
                    poi.preloadTimeout = null;
                }
            },

            // Process preload queue
            async processPreloadQueue() {
                if (this.isPreloading || this.preloadQueue.length === 0) {
                    return;
                }

                this.isPreloading = true;

                // Take the first item from queue
                const poi = this.preloadQueue.shift();

                try {
                    await this.loadPoiVideosForObject(poi, true);
                } catch (error) {
                    console.error('Preload failed for POI:', poi.id, error);
                } finally {
                    this.isPreloading = false;

                    // Process next item if queue is not empty
                    if (this.preloadQueue.length > 0) {
                        setTimeout(() => this.processPreloadQueue(), 100);
                    }
                }
            },

            async loadPoiVideos(poiId, isPreload = false) {
                if (this.selectedPoi.mux_videos) {
                    // Videos already loaded
                    return;
                }

                // Skip AJAX call if no videos
                if (this.selectedPoi.video_count === 0) {
                    this.selectedPoi.mux_videos = [];
                    return;
                }

                // Only show loading state if not preloading
                if (!isPreload) {
                    this.selectedPoi.loading_videos = true;
                }

                try {
                    const params = new URLSearchParams({
                        action: 'get_poi_videos',
                        nonce: window.mapAjax.nonce,
                        poi_id: poiId,
                    });

                    const response = await fetch(window.mapAjax.ajaxurl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: params.toString(),
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const json = await response.json();

                    if (json.success) {
                        this.selectedPoi.mux_videos = json.data;
                    } else {
                        console.error('Failed to load videos:', json.data);
                        this.selectedPoi.mux_videos = [];
                        // Show user-friendly error message only if not preloading
                        if (!isPreload) {
                            this.showError(
                                'Nem sikerült betölteni a videókat. Kérjük, próbálja újra.'
                            );
                        }
                    }
                } catch (error) {
                    console.error('Error loading videos:', error);
                    this.selectedPoi.mux_videos = [];
                    // Show user-friendly error message only if not preloading
                    if (!isPreload) {
                        this.showError('Hálózati hiba történt a videók betöltése közben.');
                    }
                } finally {
                    // Only hide loading state if not preloading
                    if (!isPreload) {
                        this.selectedPoi.loading_videos = false;
                    }
                }
            },

            // Load videos for a specific POI object (for preloading)
            async loadPoiVideosForObject(poi, isPreload = false) {
                if (poi.mux_videos) {
                    // Videos already loaded
                    return;
                }

                // Skip AJAX call if no videos
                if (poi.video_count === 0) {
                    poi.mux_videos = [];
                    return;
                }

                try {
                    const params = new URLSearchParams({
                        action: 'get_poi_videos',
                        nonce: window.mapAjax.nonce,
                        poi_id: poi.id,
                    });

                    const response = await fetch(window.mapAjax.ajaxurl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: params.toString(),
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const json = await response.json();

                    if (json.success) {
                        poi.mux_videos = json.data;
                    } else {
                        console.error('Failed to load videos:', json.data);
                        poi.mux_videos = [];
                    }
                } catch (error) {
                    console.error('Error loading videos:', error);
                    poi.mux_videos = [];
                }
            },

            showError(message) {
                // Simple error notification - could be enhanced with a proper notification system
                if (typeof window.showNotification === 'function') {
                    window.showNotification(message, 'error');
                } else {
                    alert(message);
                }
            },

            // VIDEO MODAL
            playVideo(video) {
                this.activeVideo = video;
                this.showVideoModal = true;
            },

            closeVideoModal() {
                this.showVideoModal = false;
                this.activeVideo = {};
            },
            downloadVideo(video) {
                // Ha vannak előre elkészített letöltési URL-ek, használjuk azokat
                if (video.download_urls && typeof video.download_urls === 'object') {
                    const downloadUrls = Object.values(video.download_urls);

                    // Próbáljuk meg az első (legjobb minőségű) URL-t
                    if (downloadUrls.length > 0) {
                        window.open(downloadUrls[0], '_blank');
                        return;
                    }
                }

                // Fallback: építsük fel az URL-t régi módon (ha valami hiba van)
                const filename =
                    video.title && video.title.trim() !== ''
                        ? video.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()
                        : `video_${video.index || 'unnamed'}`;

                window.open(
                    `https://stream.mux.com/${video.playback_id}/capped-1080p.mp4?download=${filename}.mp4`,
                    '_blank'
                );
            },

            formatDuration(sec = 0) {
                // Handle null, undefined, or non-numeric values
                if (sec === null || sec === undefined || isNaN(sec) || sec <= 0) {
                    return null; // Return null for invalid durations
                }

                // Convert to number and handle decimal seconds
                const seconds = parseFloat(sec);
                const m = Math.floor(seconds / 60);
                const s = Math.floor(seconds % 60);
                return `${m}:${String(s).padStart(2, '0')}`;
            },

            hasVideos(poi) {
                return poi.video_count && poi.video_count > 0;
            },
        }));
    }

    if (window.Alpine && Alpine.version) {
        registerMapModule();
    } else {
        document.addEventListener('alpine:init', registerMapModule);
    }
})();
